/**
 * useAssistantThreads — the org-shared list of assistant conversations for
 * the panel's thread view, with per-row archive.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

export type AssistantThread = {
  id: string;
  title: string | null;
  status: 'active' | 'archived';
  updatedAt: string;
  lastMessageAt: string | null;
  latestMessage: { role: string; content: string | null } | null;
  messageCount: number;
};

const THREADS_KEY = ['assistant', 'threads'];

async function fetchThreads(includeArchived: boolean): Promise<AssistantThread[]> {
  const params = new URLSearchParams({ entityType: 'assistant', pageSize: '50' });
  if (includeArchived) params.set('includeArchived', 'true');
  const res = await fetch(`${API_URL}/conversations?${params}`, { credentials: 'include' });
  if (!res.ok) throw await toApiError(res, 'Failed to load assistant chats');
  const json = (await res.json()) as { ok: boolean; data: AssistantThread[] };
  return json.data;
}

export function useAssistantThreads(options: { enabled: boolean; includeArchived: boolean }) {
  const queryClient = useQueryClient();

  const threadsQuery = useQuery({
    queryKey: [...THREADS_KEY, options.includeArchived],
    queryFn: () => fetchThreads(options.includeArchived),
    enabled: options.enabled,
    staleTime: 15_000,
  });

  const archiveMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      const res = await fetch(`${API_URL}/conversations/${conversationId}/archive`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to archive chat');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: THREADS_KEY }),
  });

  return {
    threads: threadsQuery.data ?? [],
    isLoading: threadsQuery.isLoading,
    archiveThread: (conversationId: string) => archiveMutation.mutate(conversationId),
  };
}
